import { Handle, Position } from "@xyflow/react"

const KIND_STYLES = {
  input:     "from-cyan-500 to-blue-600",
  processor: "from-blue-500 to-indigo-600",
  output:    "from-emerald-500 to-teal-600",
  unknown:   "from-zinc-400 to-zinc-600"
}

function NotConfiguredTitle() {
  return (
    <p className="text-sm font-semibold truncate inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
      <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      Not configured
    </p>
  )
}

function InputSummary({ summary }) {
  if (!summary || summary.count == null) return null
  const word = summary.count === 1 ? summary.label.replace(/s$/, "") : summary.label
  return (
    <div className="mt-2 pt-2 border-t border-border/60 text-[10px] font-mono text-muted-foreground">
      <span className="font-semibold text-foreground/80">{summary.count}</span>{" "}
      <span className="uppercase tracking-wider opacity-80">{word}</span>
    </div>
  )
}

// `selected` is provided by React Flow when a node is selected on the canvas.
// Layered states: amber border when incomplete (regardless of selection),
// primary ring when selected. Hover-glow suppressed for selected/incomplete
// nodes so it doesn't fight the explicit state.
//
// Card shape is always: small type tag on top, bold value row below. The
// value row swaps between the configured summary and a "Not configured"
// warning, so the type label keeps its place in both states.
export default function StepNode({ data, selected }) {
  const accent = KIND_STYLES[data.kind] ?? KIND_STYLES.unknown
  const incomplete = data.configComplete === false

  const borderClass = incomplete ? "border-amber-400/70" : "border-border"
  const ringClass = selected
    ? "ring-2 ring-primary ring-offset-1 ring-offset-card"
    : (incomplete ? "" : "hover:shadow-glow hover:border-primary/60")

  const extras = data.configExtras ?? []

  return (
    <div className={`rounded-xl border bg-card shadow-md w-[220px] overflow-hidden transition-all ${borderClass} ${ringClass}`}>
      <div className={`h-1 bg-gradient-to-r ${accent}`} />
      <div className="p-3">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
          {data.stepTypeLabel}
        </p>
        {data.configSummary
          ? <p className="text-sm font-semibold truncate">{data.configSummary}</p>
          : <NotConfiguredTitle />}
        {extras.length > 0 && (
          <dl className="mt-2 pt-2 border-t border-border/60 space-y-0.5">
            {extras.map(e => {
              const tone = e.tone === "warning"
                ? "text-amber-500 dark:text-amber-400"
                : "text-foreground/80"
              return (
                <div key={e.label} className="flex items-baseline justify-between gap-2 text-[10px]">
                  <dt className="text-muted-foreground uppercase tracking-wider">{e.label}</dt>
                  <dd className={`truncate ${tone}`}>{e.value}</dd>
                </div>
              )
            })}
          </dl>
        )}
        {data.kind === "input" && <InputSummary summary={data.inputSummary} />}
      </div>
      {data.kind !== "input"  && <Handle type="target" position={Position.Left} />}
      {data.kind !== "output" && <Handle type="source" position={Position.Right} />}
    </div>
  )
}
